import { Component, computed, inject, OnInit, signal } from '@angular/core';

import { ModalService } from '@services/components/ModalService/modal.service';
import { LandingService } from '@services/landing/landing.service';
import { Product } from '@services/products/interfaces/product.interfaces';
import {
  GenericConfirmModalComponent,
  GenericConfirmModalData,
} from '@shared/components/UI/generic-confirm-modal/generic-confirm-modal.component';
import { STATUS } from '@shared/interfaces/global.interface';

import {
  SelectProductModalComponent,
  SelectProductModalData,
} from './select-product-modal/select-product-modal.component';

import { CDN_PRODUCTS_URL } from '@constants/constants';
import { REMOVE_FEATURED_CONFIRM_MODAL_CONFIG, SELECT_PRODUCT_MODAL_CONFIG } from '@constants/modal.constants';
import { LANDING_MANAGEMENT_TEXTS } from '@constants/text.constants';

const FEATURED_SLOTS = 3;

@Component({
  selector: 'app-landing-managment',
  imports: [],
  templateUrl: './landing-managment.html',
  styleUrl: './landing-managment.css',
})
export class LandingManagment implements OnInit {
  public isLoading = signal(true);
  public readonly texts = LANDING_MANAGEMENT_TEXTS;

  public readonly featuredSlots = computed<(Product | null)[]>(() => {
    const products = this._featuredProducts();
    const slots: (Product | null)[] = Array.from({ length: FEATURED_SLOTS }, () => null);
    products.forEach((product) => {
      const idx = (product.position ?? 0) - 1;
      if (idx >= 0 && idx < FEATURED_SLOTS) {
        slots[idx] = product;
      }
    });
    return slots;
  });

  public readonly plantProduct = computed<Product | null>(() => this._plantProduct());

  private readonly _featuredProducts = signal<Product[]>([]);
  private readonly _plantProduct = signal<Product | null>(null);
  private readonly landingService = inject(LandingService);
  private readonly modalService = inject(ModalService);

  ngOnInit(): void {
    this.loadLandingData();
  }

  public onEditFeaturedProduct(slotIndex: number): void {
    this.openSelectProductModal('featured', slotIndex);
  }

  public onAddFeaturedProduct(slotIndex: number): void {
    this.openSelectProductModal('featured', slotIndex);
  }

  public onEditPlantProduct(): void {
    this.openSelectProductModal('plant', 0);
  }

  public onAddPlantProduct(): void {
    this.openSelectProductModal('plant', 0);
  }

  public isComingSoon(product: Product): boolean {
    return product.status === STATUS.COMING_SOON;
  }

  public onRemoveFeaturedProduct(slotIndex: number): void {
    this.openRemoveConfirmModal(() => {
      this.landingService.removeFeatured({ position: slotIndex + 1 }).subscribe(() => {
        this.loadLandingData();
      });
    });
  }

  public onRemovePlantProduct(): void {
    this.openRemoveConfirmModal(() => {
      this.landingService.removeFeatured({ isMandala: true }).subscribe(() => {
        this.loadLandingData();
      });
    });
  }

  private openRemoveConfirmModal(onConfirm: () => void): void {
    const data: GenericConfirmModalData = {
      message: '¿Estás seguro de que quieres eliminar este producto de la página principal? El espacio quedará vacío hasta que asignes uno nuevo.',
      primaryButtonLabel: 'Eliminar',
      cancelButtonLabel: 'Cancelar',
    };

    this.modalService
      .openModal<boolean, GenericConfirmModalData>({
        ...REMOVE_FEATURED_CONFIRM_MODAL_CONFIG,
        component: GenericConfirmModalComponent,
        data,
      })
      .subscribe((event) => {
        if (event.reason === 'confirm') {
          onConfirm();
        }
      });
  }

  private openSelectProductModal(slotType: SelectProductModalData['slotType'], slotIndex: number): void {
    const currentProductId = slotType === 'plant'
      ? (this._plantProduct()?.id ?? null)
      : (this.featuredSlots()[slotIndex]?.id ?? null);

    const plantId = this._plantProduct()?.id;
    const allOccupiedIds = [
      ...this._featuredProducts().map((product) => product.id),
      ...(plantId ? [plantId] : []),
    ];

    const data: SelectProductModalData = {
      slotType,
      slotIndex,
      currentProductId,
      occupiedProductIds: allOccupiedIds,
    };

    this.modalService
      .openModal<void, SelectProductModalData>({
        ...SELECT_PRODUCT_MODAL_CONFIG,
        component: SelectProductModalComponent,
        data,
      })
      .subscribe((event) => {
        if (event.reason === 'confirm') {
          this.loadLandingData();
        }
      });
  }

  private loadLandingData(): void {
    this.isLoading.set(true);
    this.landingService.overview().subscribe((data) => {
      this._featuredProducts.set(
        (data.productsToShow ?? []).map((product) => this.normalizeImageUrl(product)),
      );
      const plant = data.plantProduct ? this.normalizeImageUrl(data.plantProduct) : null;
      this._plantProduct.set(plant);
      this.isLoading.set(false);
    });
  }

  private normalizeImageUrl(product: Product): Product {
    if (!product.imageUrl || /^https?:\/\//i.test(product.imageUrl)) {
      return product;
    }
    return { ...product, imageUrl: `${CDN_PRODUCTS_URL}${product.imageUrl}` };
  }
}
